export type ShopSortOption = 'popularity' | 'price-asc' | 'price-desc' | 'newest'

export type ShopFilters = Record<string, string[] | [number, number]>

export const SHOP_SORT_OPTIONS: ShopSortOption[] = [
  'popularity',
  'price-asc',
  'price-desc',
  'newest',
]

const isShopSort = (value: string | null): value is ShopSortOption =>
  SHOP_SORT_OPTIONS.includes(value as ShopSortOption)

const parseRangeToken = (value: string): [number, number] | null => {
  const match = value.match(/^(-?\d+(?:\.\d+)?):(-?\d+(?:\.\d+)?)$/)
  if (!match) return null
  return [Number(match[1]), Number(match[2])]
}

export const parseShopSearch = (
  search: string,
): { filters: ShopFilters; sort: ShopSortOption } => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const sortParam = params.get('sort')
  const sort: ShopSortOption = isShopSort(sortParam) ? sortParam : 'popularity'

  const filters: ShopFilters = {}
  params.forEach((raw, key) => {
    if (key === 'sort' || !raw) return
    const range = parseRangeToken(raw)
    if (range) {
      filters[key] = range
      return
    }
    filters[key] = raw.split(',').filter(Boolean)
  })

  return { filters, sort }
}

export const buildShopSearch = (
  filters: ShopFilters,
  sort: ShopSortOption = 'popularity',
): URLSearchParams => {
  const params = new URLSearchParams()
  if (sort !== 'popularity') {
    params.set('sort', sort)
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (!Array.isArray(value) || value.length === 0) return
    if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
      params.set(key, `${value[0]}:${value[1]}`)
      return
    }
    const list = (value as string[]).filter(Boolean)
    if (list.length > 0) {
      params.set(key, list.join(','))
    }
  })

  return params
}

export const countActiveShopFilters = (
  filters: ShopFilters,
  schema?: Record<string, { from: number; to: number } | { values: unknown[] }>,
): number => {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (!Array.isArray(value)) return count
    if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
      const def = schema?.[key]
      if (def && 'from' in def && value[0] === def.from && value[1] === def.to) {
        return count
      }
      return count + 1
    }
    return count + ((value as string[]).length > 0 ? 1 : 0)
  }, 0)
}
